import { useEffect, useState } from 'react';

// Reads a saved value, falling back to `initial` when there is none, storage
// is blocked, what was saved no longer has the shape the caller expects (an
// array filter that used to be a string, say), or `valid` turns it down (a
// chip that has since been retired).
const readStored = (key, initial, valid) => {
    try {
        const raw = localStorage.getItem(key);
        if (raw == null) return initial;
        const v = JSON.parse(raw);
        if (Array.isArray(initial) ? !Array.isArray(v) : (initial != null && typeof v !== typeof initial)) return initial;
        if (valid && !valid(v)) return initial;
        return v;
    } catch (_) {
        return initial;
    }
};

// useState that survives a page refresh: the value is read from localStorage
// under `key` and written back each time it's set. Only a set writes — a value
// left at its default stays unsaved, so a new default in code still reaches
// people who never touched it. A new key (another job's filters) reads that
// key's saved value rather than carrying the last one over.
const usePersistedState = (key, initial, valid) => {
    const [slot, setSlot] = useState(() => ({ key, value: readStored(key, initial, valid), dirty: false }));
    const current = slot.key === key ? slot : { key, value: readStored(key, initial, valid), dirty: false };
    if (current !== slot) setSlot(current);
    useEffect(() => {
        if (!slot.dirty) return;
        try { localStorage.setItem(slot.key, JSON.stringify(slot.value)); } catch (_) { /* storage blocked or full — the value just won't survive a refresh */ }
    }, [slot]);
    const setValue = (next) => setSlot((s) => ({
        key: s.key,
        value: typeof next === 'function' ? next(s.value) : next,
        dirty: true,
    }));
    return [current.value, setValue];
};

export default usePersistedState;
